/**
 * StoreViewer — the store detail page's data browser, ADAPTER-REGISTRY pattern.
 *
 * One generic host; behavior is a pure function of the store TYPE. Each adapter
 * is self-contained (knows its read endpoint + renders itself), so adding a new
 * browsable store type = ONE entry in ADAPTERS, zero host changes. Mirrors the
 * self-host dashboard's STORE_VIEWERS and the backend store-types.js
 * `viewer.mode` declaration:
 *
 *   sqlite   → Adminer-style two-pane SQL GUI: table list (sqlite_master) +
 *              highlighted SQL editor + sortable/paginated grid, whole-table
 *              CSV export, and — when the caller may write — inline cell edit.
 *   dataset  → one implicit row set (first page), client-sortable.
 *   file     → complete path list (cursor-followed) → click → text preview.
 *   docs     → the Markdown Archive reader: doc list + rendered markdown with
 *              a line-number gutter; auto-opens the first document.
 *   postgres → knowledge base: semantic search box (self-host-only type today;
 *              the adapter ships so the page needs zero changes when the cloud
 *              sidecar backend lands).
 *
 * Every read is bounded + goes through the token-gated, tenant-forced datasets
 * API. The ONLY write surface is sqlite inline editing (a bound prepared
 * UPDATE the backend gates); everything else stays read-only.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ActionAlert } from '../../components/atoms';
import Stores from '../../services/stores';
import SqlEditor from './SqlEditor';
import SortableGrid from './SortableGrid';

const PAGE = 50;

// ── CSV export ────────────────────────────────────────────────────────────────
function toCsv(columns, rows) {
  const cell = (v) => {
    const s = v == null ? '' : (typeof v === 'object' ? JSON.stringify(v) : String(v));
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  let out = (columns || []).map(cell).join(',');
  (rows || []).forEach((r) => {
    out += `\n${(columns || []).map((c, i) => cell(Array.isArray(r) ? r[i] : r[c])).join(',')}`;
  });
  return out;
}
function downloadCsv(filename, text) {
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// base64 → UTF-8 (atob alone mangles multi-byte characters).
const b64utf8 = (b64) => {
  try {
    return new TextDecoder().decode(Uint8Array.from(atob(b64), (c) => c.charCodeAt(0)));
  } catch {
    return '';
  }
};

// ── Minimal, XSS-safe Markdown → HTML for the docs (Markdown Archive) viewer.
// The source is HTML-ESCAPED FIRST, then a small set of patterns is applied, so
// no ingested doc content can inject markup. Deliberately narrow: headings,
// bold, inline code, http(s) links — line structure is kept via one row per
// source line with a numbered gutter (the document reads as a file).
const escMd = (s) => String(s).replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
export function renderMarkdown(src) {
  let s = escMd(String(src == null ? '' : src));
  s = s
    .replace(/^(#{1,6})\s?(.*)$/gm, (_, h, t) => `<b class="md-h md-h${Math.min(6, h.length)}">${t}</b>`)
    .replace(/\*\*([^*\n]+)\*\*/g, '<b>$1</b>')
    .replace(/`([^`\n]+)`/g, '<code>$1</code>')
    .replace(/\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
  // One row per SOURCE line. Safe to split on "\n" AFTER the rewrites above
  // because none of them can span a newline.
  const lines = s.split('\n');
  const body = lines
    .map((t, i) => `<div class="md-line"><span class="md-ln">${i + 1}</span><span class="md-lt">${t}</span></div>`)
    .join('');
  return `<div class="md-body md-lines" style="--md-ln-w:${String(lines.length).length}ch">${body}</div>`;
}

const sanId = (x) => String(x).replace(/[^A-Za-z0-9_]/g, '');
const isNumericType = (t) => /INT|REAL|FLOA|DOUB|NUM|DEC/i.test(String(t || ''));

// "Nothing here yet" — the SAME shape as the stores LIST's empty state
// (DataTable: ghost skeleton rows + the centered ActionAlert card). A lone
// grey sentence under a populated header reads like a rendering failure;
// the ghosts show the shape the content will take and the card says what to
// do about it.
function ViewerEmptyState({ title, description }) {
  // Three STATIC ghost rows — an empty state is a resting page, so the bars
  // don't pulse (pulsing means "loading"; here nothing is coming).
  const ghostWidths = [46, 60, 40];
  return (
    <div className="sv-empty-state">
      <div className="sv-empty-card">
        <ActionAlert title={title} description={description} />
      </div>
      <div aria-hidden="true">
        {ghostWidths.map((w, i) => (
          <div key={i} className="sv-ghost-row">
            <div className="store-skel store-skel--static" style={{ width: 90, height: 12 }} />
            <div className="store-skel store-skel--static" style={{ width: `${w}%`, height: 12 }} />
            <div className="store-skel store-skel--static" style={{ width: 48, height: 12 }} />
          </div>
        ))}
      </div>
    </div>
  );
}

// Shimmering placeholder lines — every viewer's loading state (never a bare
// "Loading…" sentence; the page shows the SHAPE the content will take).
function SkelLines({ rows = 5, height = 12, widths }) {
  const ws = widths || [92, 100, 78, 96, 70, 88, 64, 90];
  return (
    <div>
      {Array.from({ length: rows }, (_, i) => (
        <div
          key={i}
          className="store-skel"
          style={{ width: `${ws[i % ws.length]}%`, height, marginBottom: 10 }}
        />
      ))}
    </div>
  );
}

// A markdown page's own rhythm — a heading, paragraph runs, and the blank
// source lines between them — inside the SAME numbered-gutter geometry the
// rendered document uses, so the reading pane keeps its shape while a document
// loads instead of blanking to a "Loading…" sentence.
const DOC_SKEL_PLAN = [
  [38, 16], [0, 0], [96, 10], [88, 10], [72, 10], [0, 0],
  [30, 13], [0, 0], [92, 10], [99, 10], [64, 10], [0, 0],
  [84, 10], [76, 10], [58, 10], [0, 0], [46, 13], [0, 0],
  [90, 10], [70, 10], [97, 10], [82, 10], [0, 0], [34, 13],
  [0, 0], [88, 10], [94, 10], [66, 10], [78, 10], [0, 0],
  [91, 10], [73, 10], [52, 10],
];
function SkelDoc() {
  return (
    <div className="md-body md-lines" style={{ '--md-ln-w': '2ch' }} role="status" aria-busy="true" aria-label="Loading document">
      {DOC_SKEL_PLAN.map(([w, h], i) => (
        <div key={i} className="md-line">
          <span className="md-ln">
            <i className="store-skel" style={{ display: 'inline-block', width: 10, height: 8, verticalAlign: 'middle' }} />
          </span>
          <span className="md-lt">
            {w ? <div className="store-skel" style={{ width: `${w}%`, height: h, margin: '3px 0' }} /> : null}
          </span>
        </div>
      ))}
    </div>
  );
}

// Search hits: the source line over the matched chunk — the shape a hit takes.
function SkelHits({ rows = 5 }) {
  const wide = [96, 88, 74, 92, 68];
  const narrow = [62, 54, 80, 46, 70];
  return (
    <div role="status" aria-busy="true" aria-label="Searching">
      {Array.from({ length: rows }, (_, i) => (
        <div key={i} className="sv-hit">
          <div className="store-skel" style={{ width: 140 + (i % 3) * 40, height: 9, marginBottom: 8 }} />
          <div className="store-skel" style={{ width: `${wide[i % 5]}%`, height: 10, marginBottom: 6 }} />
          <div className="store-skel" style={{ width: `${narrow[i % 5]}%`, height: 10 }} />
        </div>
      ))}
    </div>
  );
}

// ── SQLite → Adminer/phpMyAdmin-style two-pane SQL GUI ───────────────────────
// LEFT = the table list (sqlite_master); RIGHT = a highlighted SQL editor +
// sortable, paginated results grid. Clicking a table loads `SELECT * FROM "<t>"
// [ORDER BY …] LIMIT n OFFSET m` AND mirrors it into the editor; the editor
// runs ANY read-only query (server enforces readOnly) into the same grid.
// Header click = SERVER-side whole-table sort; ← / → page the table view; with
// write access, double-click a cell edits in place (rowid-bound UPDATE).
function SqliteBrowser({ token, store, canWrite, exportRef, notify }) {
  const [tables, setTables] = useState(null);
  const [err, setErr] = useState(null);
  const [runErr, setRunErr] = useState(null);
  const [sqlText, setSqlText] = useState('');
  const [loading, setLoading] = useState(false);
  // Table-view result: { custom, table, offset, hasNext, order, columns, rows,
  // rids (write mode), colTypes } — custom queries carry only columns/rows.
  const [result, setResult] = useState(null);
  // Live mirror of the table-view state for the document-level key handler +
  // the export hook (both outlive any one render's closures).
  const stRef = useRef({ table: null, offset: 0, hasNext: false, order: null });
  const lastCustomRef = useRef(null);
  const typeCacheRef = useRef({});
  const suggestRef = useRef(new Set());

  useEffect(() => {
    let alive = true;
    Stores.sql(token, store.storeId,
      "SELECT name FROM sqlite_master WHERE type IN ('table','view') AND name NOT LIKE 'sqlite_%' ORDER BY name LIMIT 200")
      .then((r) => {
        if (!alive) return;
        const iN = (r.columns || []).indexOf('name');
        const t = (r.rows || []).map((row) => row[iN]);
        t.forEach((n) => suggestRef.current.add(n));
        setTables(t);
      })
      .catch((e) => alive && setErr(e.message));
    return () => { alive = false; };
  }, [token, store.storeId]);

  const ensureTypes = useCallback(async (name) => {
    if (typeCacheRef.current[name]) return typeCacheRef.current[name];
    try {
      const r = await Stores.sql(token, store.storeId, `PRAGMA table_info("${sanId(name)}")`);
      const iN = (r.columns || []).indexOf('name');
      const iT = (r.columns || []).indexOf('type');
      const m = {};
      (r.rows || []).forEach((row) => { m[row[iN]] = String(row[iT] || '').toUpperCase(); });
      typeCacheRef.current[name] = m;
    } catch {
      typeCacheRef.current[name] = {};
    }
    return typeCacheRef.current[name];
  }, [token, store.storeId]);

  const loadTable = useCallback(async (name, offset, orderOverride) => {
    // Keep the current sort when re-paging the SAME table; reset it on switch.
    const order = orderOverride !== undefined
      ? orderOverride
      : (name === stRef.current.table ? stRef.current.order : null);
    const ord = order ? ` ORDER BY "${sanId(order.col)}" ${order.dir > 0 ? 'ASC' : 'DESC'}` : '';
    const tail = `${ord} LIMIT ${PAGE}${offset ? ` OFFSET ${offset}` : ''}`;
    const displaySql = `SELECT * FROM "${sanId(name)}"${tail}`;
    setSqlText(displaySql);
    setRunErr(null);
    setLoading(true);
    const types = await ensureTypes(name);
    // With write access, fetch the hidden rowid so cells can be edited in
    // place — the editor still shows the clean SELECT. Views have no rowid →
    // that fetch errors → fall back to a plain (read-only) load.
    const paint = (cols, rows, rids) => {
      cols.forEach((c) => suggestRef.current.add(c));
      stRef.current = { table: name, offset, hasNext: rows.length === PAGE, order };
      setResult({
        custom: false,
        table: name,
        offset,
        hasNext: rows.length === PAGE,
        order,
        columns: cols,
        rows,
        rids,
        colTypes: cols.map((c) => types[c] || ''),
      });
      setLoading(false);
    };
    try {
      if (canWrite) {
        try {
          const r = await Stores.sql(token, store.storeId, `SELECT rowid AS _rid, * FROM "${sanId(name)}"${tail}`);
          const cols = r.columns || [];
          const rows = r.rows || [];
          if (cols[0] === '_rid') {
            paint(cols.slice(1), rows.map((row) => row.slice(1)), rows.map((row) => row[0]));
            return;
          }
          paint(cols, rows, null);
          return;
        } catch {
          // fall through to the plain read-only load (e.g. a VIEW)
        }
      }
      const r = await Stores.sql(token, store.storeId, displaySql);
      paint(r.columns || [], r.rows || [], null);
    } catch (e) {
      setRunErr(e.message);
      setResult(null);
      setLoading(false);
    }
  }, [token, store.storeId, canWrite, ensureTypes]);

  useEffect(() => {
    if (tables && tables.length) loadTable(tables[0], 0, null);
  }, [tables, loadTable]);

  const runEditor = useCallback(() => {
    const sql = (sqlText || '').trim();
    if (!sql) return;
    stRef.current = { table: null, offset: 0, hasNext: false, order: null };
    setRunErr(null);
    setLoading(true);
    Stores.sql(token, store.storeId, sql)
      .then((r) => {
        const cols = r.columns || [];
        const rows = r.rows || [];
        cols.forEach((c) => suggestRef.current.add(c));
        lastCustomRef.current = { columns: cols, rows };
        setResult({ custom: true, columns: cols, rows, rids: null, colTypes: null });
      })
      .catch((e) => { setRunErr(e.message); })
      .finally(() => setLoading(false));
  }, [token, store.storeId, sqlText]);

  // ← / → paginate the current TABLE view (ignored while typing).
  useEffect(() => {
    const onKey = (e) => {
      const tg = e.target;
      if (tg && (tg.tagName === 'TEXTAREA' || tg.tagName === 'INPUT')) return;
      const st = stRef.current;
      if (!st.table) return;
      if (e.key === 'ArrowRight' && st.hasNext) { e.preventDefault(); loadTable(st.table, st.offset + PAGE); }
      else if (e.key === 'ArrowLeft' && st.offset > 0) { e.preventDefault(); loadTable(st.table, Math.max(0, st.offset - PAGE)); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [loadTable]);

  // Export the CURRENT view to CSV — the whole table (respecting sort) when
  // browsing a table, else the current custom-query result set.
  useEffect(() => {
    if (!exportRef) return undefined;
    exportRef.current = () => {
      const st = stRef.current;
      if (st.table) {
        const ord = st.order ? ` ORDER BY "${sanId(st.order.col)}" ${st.order.dir > 0 ? 'ASC' : 'DESC'}` : '';
        Stores.sql(token, store.storeId, `SELECT * FROM "${sanId(st.table)}"${ord}`)
          .then((r) => downloadCsv(`${store.name || st.table}.csv`, toCsv(r.columns || [], r.rows || [])))
          .catch((e) => notify && notify(`Export failed: ${e.message}`, 'error'));
      } else if (lastCustomRef.current && lastCustomRef.current.columns.length) {
        downloadCsv(`${store.name || 'query'}.csv`, toCsv(lastCustomRef.current.columns, lastCustomRef.current.rows));
      } else if (notify) {
        notify('Nothing to export — select a table or run a query first.', 'warning');
      }
    };
    return () => { exportRef.current = null; };
  }, [exportRef, token, store.storeId, store.name, notify]);

  if (err) return <div className="sv-empty">Could not read: {err}</div>;
  if (!tables) {
    // Two-pane skeleton mirroring the browser's real layout.
    return (
      <div className="sdb">
        <div className="sdb-tables">
          <div className="sdb-tables-label">Tables</div>
          <SkelLines rows={4} height={22} widths={[100, 100, 100, 100]} />
        </div>
        <div className="sdb-main">
          <div className="store-skel" style={{ height: 104, borderRadius: 8 }} />
          <div style={{ marginTop: 14 }}>
            <SkelLines rows={6} />
          </div>
        </div>
      </div>
    );
  }

  const onSort = result && !result.custom
    ? (i) => {
      const cn = result.columns[i];
      const cur = result.order;
      const order = cur && cur.col === cn ? { col: cn, dir: -cur.dir } : { col: cn, dir: 1 };
      loadTable(result.table, 0, order);
    }
    : undefined;

  const onEdit = result && !result.custom && result.rids
    ? async (ri, ci, nv) => {
      // COERCE to the column's type so a numeric column stores a real number
      // (42), not text ("42") — SQLite affinity keeps text as text. The grid
      // already rejected a non-numeric string for a numeric column.
      const ty = (result.colTypes && result.colTypes[ci]) || '';
      const bound = isNumericType(ty) && String(nv).trim() !== '' ? Number(nv) : nv;
      try {
        await Stores.sqlWrite(
          token, store.storeId,
          `UPDATE "${sanId(result.table)}" SET "${sanId(result.columns[ci])}" = ? WHERE rowid = ?`,
          [bound, result.rids[ri]],
        );
        if (notify) notify(`Saved ${result.columns[ci]}`, 'success');
      } catch (e) {
        if (notify) notify(`Save failed: ${e.message}`, 'error');
        throw e;
      }
    }
    : undefined;

  return (
    <div className="sdb">
      <div className="sdb-tables">
        <div className="sdb-tables-label">Tables</div>
        {tables.length
          ? tables.map((t) => (
            <button
              key={t}
              type="button"
              className={`sdb-t${result && !result.custom && result.table === t ? ' on' : ''}`}
              title={t}
              onClick={() => loadTable(t, 0)}
            >
              {t}
            </button>
          ))
          : <div className="sv-empty">No tables yet.</div>}
      </div>
      <div className="sdb-main">
        <SqlEditor
          value={sqlText}
          onChange={setSqlText}
          onRun={runEditor}
          placeholder={tables.length ? `SELECT * FROM "${tables[0]}" …` : 'SELECT …  (read-only)'}
          getSuggestions={() => Array.from(suggestRef.current)}
        />
        {runErr && <div className="sdb-err">{runErr}</div>}
        <div className="sdb-results">
          {loading ? <SkelLines rows={8} />
            : !result && !tables.length ? <ViewerEmptyState title="Empty database" description="Tables appear here once an agent creates them in this store." />
            : !result ? <div className="sv-empty">Select a table or run a query.</div>
              : (
                <>
                  <SortableGrid
                    columns={result.columns}
                    rows={result.rows}
                    rowStart={result.custom ? 1 : result.offset + 1}
                    colTypes={result.custom ? null : result.colTypes}
                    sortCol={result.order ? result.columns.indexOf(result.order.col) : -1}
                    sortDir={result.order ? result.order.dir : 1}
                    onSort={onSort}
                    onEdit={onEdit}
                    onTypeError={(msg) => notify && notify(`Type mismatch: ${msg}`, 'error')}
                  />
                  {!result.custom && (
                    <div className="sv-nav">
                      {result.offset > 0
                        ? <button type="button" className="sv-link" onClick={() => loadTable(result.table, Math.max(0, result.offset - PAGE))}>‹ prev</button>
                        : <span />}
                      <span className="sv-muted">
                        rows {result.rows.length ? result.offset + 1 : 0}–{result.offset + result.rows.length}
                        {'  ·  ← / → to page'}
                        {result.rids ? '  ·  double-click a cell to edit' : ''}
                      </span>
                      {result.hasNext
                        ? <button type="button" className="sv-link" onClick={() => loadTable(result.table, result.offset + PAGE)}>next ›</button>
                        : <span />}
                    </div>
                  )}
                </>
              )}
        </div>
      </div>
    </div>
  );
}

// ── Dataset → one implicit row set (first page), client-sortable ─────────────
function DatasetViewer({ token, store }) {
  const [state, setState] = useState({ loading: true });
  useEffect(() => {
    let alive = true;
    Stores.query(token, store.storeId, { limit: PAGE })
      .then((r) => alive && setState({ rows: r.rows || [] }))
      .catch((e) => alive && setState({ err: e.message }));
    return () => { alive = false; };
  }, [token, store.storeId]);
  if (state.loading) return <SkelLines rows={6} />;
  if (state.err) return <div className="sv-empty">Could not read: {state.err}</div>;
  if (!state.rows.length) {
    return <ViewerEmptyState title="No records yet" description="Records land here once an agent appends to this store." />;
  }
  const cols = Object.keys(state.rows[0]);
  return (
    <>
      <div className="sv-muted" style={{ marginBottom: 6 }}>
        first {state.rows.length} record{state.rows.length === 1 ? '' : 's'}
      </div>
      <SortableGrid columns={cols} rows={state.rows} rowStart={1} />
    </>
  );
}

// ── File → complete path list (cursor-followed) → click → text preview ───────
function FileBrowser({ token, store }) {
  const [files, setFiles] = useState(null);
  const [truncated, setTruncated] = useState(false);
  const [err, setErr] = useState(null);
  const [sel, setSel] = useState(null);
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    let alive = true;
    Stores.listAllFiles(token, store.storeId)
      .then((r) => {
        if (!alive) return;
        setFiles(r.files || []);
        setTruncated(!!r.truncated);
      })
      .catch((e) => alive && setErr(e.message));
    return () => { alive = false; };
  }, [token, store.storeId]);
  const open = (f) => {
    setSel(f.path);
    setPreview({ loading: true });
    Stores.getFile(token, store.storeId, f.path)
      .then((g) => {
        if (g && g.binary) { setPreview({ text: 'Binary file — preview not shown.' }); return; }
        const txt = g && (g.content != null ? g.content : (g.contentBase64 ? b64utf8(g.contentBase64) : ''));
        setPreview({ text: String(txt || '').slice(0, 20000) });
      })
      .catch((e) => setPreview({ text: `Could not read file: ${e.message}` }));
  };
  if (err) return <div className="sv-empty">Could not list: {err}</div>;
  if (!files) return <SkelLines rows={6} height={20} widths={[100, 100, 100, 100]} />;
  if (!files.length) {
    return <ViewerEmptyState title="No files yet" description="Files land here once an agent writes to this store." />;
  }
  return (
    <div>
      {truncated && (
        <div className="sv-muted" style={{ marginBottom: 6 }}>
          Showing the first {files.length} files — the store holds more.
        </div>
      )}
      <div className="sv-files">
        {files.map((f) => {
          const kb = f.size >= 1024 ? `${(f.size / 1024).toFixed(1)} KB` : `${f.size} B`;
          return (
            <button key={f.path} type="button" className={`sv-file${sel === f.path ? ' on' : ''}`} onClick={() => open(f)}>
              <span className="sv-mono">{f.path}</span>
              <span className="sv-muted">{kb}</span>
            </button>
          );
        })}
      </div>
      {preview && (preview.loading
        ? <SkelLines rows={8} height={10} widths={[72, 94, 48, 86, 62, 98, 40, 78]} />
        : <pre className="sv-preview">{preview.text}</pre>)}
    </div>
  );
}

// ── Docs (Markdown Archive) — the human-readable copy of every ingested doc ──
// SAME backend driver as `file` (list → get); the content is markdown prose, so
// it renders lightly (renderMarkdown) in a fixed two-pane reader: LEFT = the
// document list (name on top, folder dimmed underneath), RIGHT = a pinned
// header (which doc you're reading) over one scrolling region.
function DocsViewer({ token, store, onDocMeta }) {
  const [files, setFiles] = useState(null);
  const [err, setErr] = useState(null);
  const [sel, setSel] = useState(null);
  const [doc, setDoc] = useState(null); // { loading } | { html } | { note }
  const scrollRef = useRef(null);
  const onDocMetaRef = useRef(onDocMeta);
  onDocMetaRef.current = onDocMeta;

  const open = useCallback((f) => {
    setSel(f.path);
    setDoc({ loading: true });
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    Stores.getFile(token, store.storeId, f.path)
      .then((g) => {
        if (g && g.binary) { setDoc({ note: 'Binary document — preview not shown.' }); return; }
        const txt = g && (g.content != null ? g.content : (g.contentBase64 ? b64utf8(g.contentBase64) : ''));
        setDoc({ html: renderMarkdown(String(txt || '')) });
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
      })
      .catch((e) => setDoc({ note: `Could not read: ${e.message}` }));
  }, [token, store.storeId]);

  useEffect(() => {
    let alive = true;
    Stores.listAllFiles(token, store.storeId)
      .then((r) => {
        if (!alive) return;
        // Hide internal sync-state bookkeeping (`.sync/*`) — the KB connector's
        // own cursors, not documents.
        const docs = ((r && r.files) || []).filter((f) => f && f.path && !f.path.startsWith('.sync/'));
        setFiles(docs);
        if (onDocMetaRef.current) onDocMetaRef.current({ count: docs.length, partial: !!(r && r.truncated) });
        // Open the first document automatically — "select a document" next to a
        // list of 47 is a click with exactly one sensible answer.
        if (docs.length) open(docs[0]);
      })
      .catch((e) => alive && setErr(e.message));
    return () => { alive = false; };
  }, [token, store.storeId, open]);

  if (err) return <div className="sv-empty">Could not list: {err}</div>;
  if (!files) return <SkelLines rows={7} height={20} widths={[100, 100, 100, 100]} />;
  if (!files.length) {
    return <ViewerEmptyState title="No documents yet" description="Documents land here once a sync or an agent ingests into this store." />;
  }
  return (
    <div className="sv-docs">
      <div className="sv-files">
        {files.map((f) => {
          const cut = f.path.lastIndexOf('/');
          const dir = cut >= 0 ? f.path.slice(0, cut) : '';
          const name = cut >= 0 ? f.path.slice(cut + 1) : f.path;
          return (
            <button key={f.path} type="button" className={`sv-file${sel === f.path ? ' on' : ''}`} title={f.path} onClick={() => open(f)}>
              <span className="sv-f-name">{name}</span>
              {dir ? <span className="sv-f-dir">{dir}</span> : null}
            </button>
          );
        })}
      </div>
      <div className="sv-body">
        <div className="sv-doc-head">{sel || 'Select a document'}</div>
        <div className="sv-doc-scroll" ref={scrollRef}>
          {!doc ? <div className="sv-empty">Select a document to read.</div>
            : doc.loading ? <SkelDoc />
              : doc.note ? <div className="sv-empty">{doc.note}</div>
                : <div dangerouslySetInnerHTML={{ __html: doc.html }} />}
        </div>
      </div>
    </div>
  );
}

// ── Postgres KB → semantic search (a vector index, not a table set) ──────────
function KbViewer({ token, store }) {
  const [q, setQ] = useState('');
  const [res, setRes] = useState(null);
  const run = () => {
    const query = q.trim();
    if (!query) return;
    setRes({ loading: true });
    Stores.query(token, store.storeId, { query, topK: 8 })
      .then((r) => setRes({ hits: (r && (r.results || r.rows || r.matches)) || [] }))
      .catch((e) => setRes({ err: e.message }));
  };
  return (
    <div>
      <div className="sv-muted" style={{ marginBottom: 8 }}>
        Knowledge base — searched semantically, not browsed as tables.
      </div>
      <div className="sv-search">
        <input
          className="sv-input"
          placeholder="Search this KB…"
          value={q}
          autoFocus
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && run()}
        />
        <button type="button" className="sv-btn" onClick={run}>Search</button>
      </div>
      {res && (res.loading ? <SkelHits />
        : res.err ? <div className="sv-empty">Search failed: {res.err}</div>
          : !res.hits.length ? <div className="sv-empty">No matches.</div>
            : res.hits.map((m, i) => {
              const chunk = m.chunk || m.text || m.content || JSON.stringify(m);
              return (
                <div key={i} className="sv-hit">
                  <div className="sv-muted" style={{ fontSize: 11 }}>
                    {String(m.sourceId || m.source || '')}{m.score != null ? ` · ${Number(m.score).toFixed(3)}` : ''}
                  </div>
                  <div className="sv-hit-text">{String(chunk).slice(0, 600)}</div>
                </div>
              );
            }))}
    </div>
  );
}

// The adapter registry — add a browsable type = one entry here.
const ADAPTERS = {
  sqlite: SqliteBrowser,
  dataset: DatasetViewer,
  file: FileBrowser,
  docs: DocsViewer,
  postgres: KbViewer,
};

/**
 * @param {object} store - the store row ({ storeId, type, name, … })
 * @param {string} projectToken - the project apiToken (datasets auth)
 * @param {boolean} [canWrite] - sqlite inline editing gate (from the GET)
 * @param {object} [exportRef] - ref the sqlite adapter arms with an export-CSV fn
 * @param {(meta: {count, partial}) => void} [onDocMeta] - docs adapter reports
 *   its document count for the page's meta grid
 * @param {(message, type) => void} [notify] - toast bridge
 */
export default function StoreViewer({ store, projectToken, canWrite, exportRef, onDocMeta, notify }) {
  const Adapter = ADAPTERS[store.type];
  if (!Adapter) return <div className="sv-empty">No viewer for this store type yet.</div>;
  if (!projectToken) return <div className="sv-empty">Could not resolve the project token.</div>;
  return (
    <div className="store-viewer">
      <Adapter
        token={projectToken}
        store={store}
        canWrite={canWrite}
        exportRef={exportRef}
        onDocMeta={onDocMeta}
        notify={notify}
      />
    </div>
  );
}
